import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ChevronRight } from 'lucide-react';

const FacebookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const TwitterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
  </svg>
);

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const LinkedinIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const Footer = () => {
  // Mapped directly to the IDs in your services.js data file
  const footerServices = [
    { name: 'Channel Letters', path: '/services/29' },
    { name: 'Monument Signs', path: '/services/27' },
    { name: 'Illuminated Signs', path: '/services/26' },
    { name: 'Vehicle Graphics', path: '/services/8' },
    { name: 'Window Decals', path: '/services/6' },
    { name: 'Digital Signs', path: '/services/18' }
  ];

  // Added Blog to the company links
  const companyLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'All Services', path: '/services' },
    { name: 'Our Portfolio', path: '/portfolio' },
    { name: 'Insights & Blog', path: '/blog' },
    { name: 'Contact Us', path: '/contact' }
  ];

  return (
    <footer className="bg-brand-dark pt-20 pb-8 border-t-4 border-brand-cyan overflow-hidden relative">

      {/* Giant Background Watermark */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden z-0 pointer-events-none select-none">
        <h1 className="text-[12rem] md:text-[18rem] lg:text-[22rem] font-heading font-black tracking-tighter watermark-shimmer whitespace-nowrap">
          A2Z SIGNS
        </h1>
      </div>

      {/* Main Content Wrapper */}
      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

          {/* Column 1: Logo Card & Bio */}
          <div className="lg:pr-6">
            <Link to="/" className="inline-block bg-white px-6 py-4 rounded-2xl shadow-lg mb-8 hover:shadow-xl transition-shadow duration-300">
              <img
                src="/best-sign-company-in-usa.png"
                alt="A2Z Signs - Best Sign Company in USA"
                className="h-12 md:h-14 w-auto object-contain"
              />
            </Link>

            <p className="text-gray-400 mb-8 leading-relaxed text-sm">
              Your trusted custom sign company in Upland, California. We provide high-quality signage solutions designed to help businesses improve visibility, branding, and customer engagement.
            </p>

            {/* Social Icons (Set to open in new tabs) */}
            <div className="flex gap-4">
              {[
                { icon: <FacebookIcon />, link: "https://www.facebook.com/a2zsigns.co/" },
                { icon: <InstagramIcon />, link: "https://www.instagram.com/a2zsigns.co/" },
                { icon: <LinkedinIcon />, link: "https://www.linkedin.com/company/a2zsigns" }
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-brand-cyan hover:text-brand-dark hover:-translate-y-1 transition-all duration-300 backdrop-blur-sm"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Services */}
          <div>
            <h4 className="text-white font-heading font-bold tracking-widest text-sm mb-2 uppercase">Services</h4>
            <div className="w-8 h-1 bg-brand-cyan mb-8"></div>

            <ul className="space-y-4">
              {footerServices.map((service) => (
                <li key={service.name}>
                  <Link to={service.path} className="group flex items-center text-gray-400 hover:text-brand-cyan transition-colors duration-300 text-sm">
                    <ChevronRight size={14} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    <span className="transform group-hover:translate-x-2 transition-transform duration-300">{service.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Company Links */}
          <div>
            <h4 className="text-white font-heading font-bold tracking-widest text-sm mb-2 uppercase">Company</h4>
            <div className="w-8 h-1 bg-brand-cyan mb-8"></div>

            <ul className="space-y-4">
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="group flex items-center text-gray-400 hover:text-brand-cyan transition-colors duration-300 text-sm">
                    <ChevronRight size={14} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    <span className="transform group-hover:translate-x-2 transition-transform duration-300">{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact Info */}
          <div>
            <h4 className="text-white font-heading font-bold tracking-widest text-sm mb-2 uppercase">Contact</h4>
            <div className="w-8 h-1 bg-brand-cyan mb-8"></div>

            <ul className="space-y-6">
              <li className="flex items-start gap-4 text-gray-400 group">
                <div className="bg-white/5 p-2 rounded-lg group-hover:bg-brand-cyan/20 transition-colors">
                  <MapPin size={20} className="text-brand-cyan flex-shrink-0" />
                </div>
                <span className="text-sm leading-relaxed mt-1">
                  1302 Monte Vista Ave #15<br />
                  Upland, CA 91786
                </span>
              </li>
              <li className="flex items-center gap-4 text-gray-400 group">
                <div className="bg-white/5 p-2 rounded-lg group-hover:bg-brand-cyan/20 transition-colors">
                  <Phone size={20} className="text-brand-cyan flex-shrink-0" />
                </div>
                <a href="tel:+19093053825" className="text-sm hover:text-brand-cyan transition-colors">
                  +1 (909) 305-3825
                </a>
              </li>
              <li className="flex items-center gap-4 text-gray-400 group">
                <div className="bg-white/5 p-2 rounded-lg group-hover:bg-brand-cyan/20 transition-colors">
                  <Mail size={20} className="text-brand-cyan flex-shrink-0" />
                </div>
                <a href="mailto:A2zsigns.co@gmail.com" className="text-sm hover:text-brand-cyan transition-colors">
                  A2zsigns.co@gmail.com
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-xs">
            &copy; {new Date().getFullYear()} A2Z Signs. All rights reserved.
          </p>
          <div className="flex gap-6 text-xs text-gray-500">
            <Link to="/privacy-policy" className="hover:text-brand-cyan transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-brand-cyan transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>

      {/* CSS for Giant Background Text Shimmer */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes sweep {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .watermark-shimmer {
          background: linear-gradient(
            120deg, 
            rgba(255, 255, 255, 0.06) 40%, 
            rgba(0, 198, 255, 0.6) 50%, 
            rgba(255, 255, 255, 0.06) 60%
          );
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
          animation: sweep 6s linear infinite;
        }
      `}} />
    </footer>
  );
};

export default Footer;